import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { whatsappLink } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared dark-theme styles for form inputs so text is clearly readable.
const FIELD_CLASS =
  "rounded-none border-white/30 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-brand-orange";

export default function BookNowDialog({ open, onOpenChange, defaultMoto }) {
  const { t, i18n } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [motos, setMotos] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    moto_type: defaultMoto || "",
    start_date: "",
    end_date: "",
    message: "",
  });

  // Sync defaultMoto when dialog re-opens with a different bike
  useEffect(() => {
    setForm((f) => ({ ...f, moto_type: defaultMoto || f.moto_type }));
  }, [defaultMoto, open]);

  // Load motos for the dropdown
  useEffect(() => {
    axios
      .get(`${API}/motos`)
      .then((r) => setMotos(r.data.motos || []))
      .catch(() => setMotos([]));
  }, []);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, language: i18n.language });
      toast.success(t("contact.success"));
      onOpenChange(false);
      setForm({
        name: "",
        email: "",
        phone: "",
        moto_type: "",
        start_date: "",
        end_date: "",
        message: "",
      });
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const waMessage = () =>
    `Hola Cascante Moto Rental! Me llamo ${form.name || "..."}. Quiero reservar ${
      form.moto_type || "una moto"
    } del ${form.start_date || "?"} al ${form.end_date || "?"}.`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="book-now-dialog"
        className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-none border-2 border-brand-orange bg-brand-black p-0 text-white"
      >
        <div className="bg-brand-orange px-6 py-5 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl uppercase tracking-tight text-white">
              {t("nav.bookNow")}
            </DialogTitle>
            <DialogDescription className="text-white/90">
              {t("contact.subtitle")}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
          <div className="md:col-span-1">
            <Label className="text-xs font-bold uppercase tracking-widest text-white">
              {t("contact.name")} *
            </Label>
            <Input
              data-testid="book-name"
              required
              value={form.name}
              onChange={setField("name")}
              className={FIELD_CLASS}
            />
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs font-bold uppercase tracking-widest text-white">
              {t("contact.email")} *
            </Label>
            <Input
              data-testid="book-email"
              required
              type="email"
              value={form.email}
              onChange={setField("email")}
              className={FIELD_CLASS}
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-white">
              {t("contact.phone")}
            </Label>
            <Input
              data-testid="book-phone"
              value={form.phone}
              onChange={setField("phone")}
              className={FIELD_CLASS}
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-white">
              {t("contact.moto")}
            </Label>
            <Select
              value={form.moto_type}
              onValueChange={(v) => setForm((f) => ({ ...f, moto_type: v }))}
            >
              <SelectTrigger
                data-testid="book-moto-select"
                className={`${FIELD_CLASS} h-10`}
              >
                <SelectValue placeholder={t("contact.moto")} />
              </SelectTrigger>
              <SelectContent className="rounded-none border-brand-orange bg-brand-black text-white">
                {motos.map((m) => (
                  <SelectItem
                    key={m.id}
                    value={m.name}
                    className="rounded-none text-white focus:bg-brand-orange focus:text-white"
                  >
                    {m.name} — ${m.price_day_usd}/day
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-white">
              {t("contact.start")}
            </Label>
            <Input
              data-testid="book-start"
              type="date"
              value={form.start_date}
              onChange={setField("start_date")}
              className={`${FIELD_CLASS} [color-scheme:dark]`}
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-white">
              {t("contact.end")}
            </Label>
            <Input
              data-testid="book-end"
              type="date"
              value={form.end_date}
              onChange={setField("end_date")}
              className={`${FIELD_CLASS} [color-scheme:dark]`}
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-white">
              {t("contact.message")}
            </Label>
            <Textarea
              data-testid="book-message"
              value={form.message}
              onChange={setField("message")}
              rows={3}
              className={FIELD_CLASS}
            />
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-3 pt-2">
            <Button
              data-testid="book-submit-btn"
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-none bg-brand-orange font-display text-xl uppercase tracking-wider text-white hover:bg-white hover:text-brand-orange"
            >
              {submitting ? "…" : t("contact.submit")}
            </Button>
            <a
              data-testid="book-whatsapp-btn"
              href={whatsappLink(waMessage())}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-none border-2 border-[#25D366] bg-transparent px-4 py-3 font-display text-xl uppercase tracking-wider text-[#25D366] hover:bg-[#25D366] hover:text-white"
            >
              {t("contact.whatsappBtn")}
            </a>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
